'use client'

/**
 * ReportDialog — the shared "report this content" modal used across every UGC
 * surface (content comments, community posts, community comments). Files a row
 * into the unified `content_reports` table (RLS: reporter_user_id = auth.uid()),
 * which surfaces in Admin → Moderation → Reports.
 *
 * Replaces the old prompt()/alert() flow: a real reason picker (never a
 * hard-coded reason) + optional notes, and it captures `reported_user_id` so
 * the admin "reports against this user" filter works.
 */

import { useState } from 'react'
import { useTranslations } from 'next-intl'
import { createClient } from '@/lib/supabase/client'
import { Button } from '@/components/ui/button'
import { ModalOverlay } from '@/components/account/membership-primitives'
import { REPORT_REASONS, REPORT_REASON_KEY, type ReportReason, type ReportContentType } from '@/lib/moderation/report-reasons'

export function ReportDialog({
  open,
  onClose,
  contentType,
  contentId,
  reportedUserId,
  currentUserId,
}: {
  open: boolean
  onClose: () => void
  contentType: ReportContentType
  contentId: string
  /** The content author — denormalized onto the report so moderators can
   *  filter by reported user. Null only for guest/deleted authors. */
  reportedUserId: string | null
  currentUserId: string
}) {
  const t = useTranslations('moderation')
  const supabase = createClient()
  const [reason, setReason] = useState<ReportReason>('inappropriate')
  const [notes, setNotes] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState('')
  const [done, setDone] = useState(false)

  const close = () => {
    if (submitting) return
    // Reset so a reopened dialog starts fresh.
    setReason('inappropriate')
    setNotes('')
    setError('')
    setDone(false)
    onClose()
  }

  const submit = async () => {
    setSubmitting(true)
    setError('')
    const { error: insertError } = await supabase.from('content_reports').insert({
      reporter_user_id: currentUserId,
      content_type: contentType,
      content_id: contentId,
      reported_user_id: reportedUserId,
      reason,
      notes: notes.trim() ? notes.trim().slice(0, 500) : null,
    })
    // 23505 = the partial-unique index (one open pending report per
    // reporter+content). Treat as success — the flag already exists.
    if (insertError && insertError.code !== '23505') {
      setError(t('error'))
      setSubmitting(false)
      return
    }
    setDone(true)
    setSubmitting(false)
  }

  return (
    <ModalOverlay open={open} onClose={close} width="max-w-md">
      <div className="p-5">
        {done ? (
          <>
            <h2 className="text-base font-semibold">{t('reportedTitle')}</h2>
            <p className="mt-2 text-sm text-muted-foreground">{t('reportedBody')}</p>
            <div className="mt-4 flex justify-end">
              <Button size="sm" onClick={close}>{t('close')}</Button>
            </div>
          </>
        ) : (
          <>
            <h2 className="text-base font-semibold">{t('reportTitle')}</h2>
            <p className="mt-1 text-sm text-muted-foreground">{t('reportLead')}</p>

            <fieldset className="mt-3">
              <legend className="mb-1.5 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                {t('reasonLabel')}
              </legend>
              <div className="space-y-1.5">
                {REPORT_REASONS.map((r) => (
                  <label key={r} className="flex items-center gap-2 text-sm">
                    <input
                      type="radio"
                      name="report-reason"
                      value={r}
                      checked={reason === r}
                      onChange={() => setReason(r)}
                    />
                    {t(REPORT_REASON_KEY[r])}
                  </label>
                ))}
              </div>
            </fieldset>

            <label className="mt-3 block">
              <span className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                {t('notesLabel')}
              </span>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                maxLength={500}
                rows={3}
                placeholder={t('notesPlaceholder')}
                className="mt-1 w-full resize-none border border-border bg-background px-3 py-2 text-sm outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-100"
              />
            </label>

            {error && <p className="mt-2 text-xs text-destructive">{error}</p>}

            <div className="mt-4 flex justify-end gap-2">
              <Button size="sm" variant="outline" onClick={close} disabled={submitting}>
                {t('cancel')}
              </Button>
              <Button size="sm" onClick={submit} disabled={submitting}>
                {submitting ? t('submitting') : t('submit')}
              </Button>
            </div>
          </>
        )}
      </div>
    </ModalOverlay>
  )
}
